import React, { Fragment, useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  FormFeedback,
  Input,
  InputGroup,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";

// redux toolkit
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { fetchCustomer, updateCustomer } from "../../slices/apps/customerSlice";

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";

// react input mask
import { IMaskInput } from "react-imask";

const UpdateModal = ({ id, isOpen, toggle }) => {
  const dispatch = useDispatch();

  const { customer, customerLoading, updateCustomerLoading } = useSelector(
    (state) => {
      return {
        customer: state.customer.customer,
        customerLoading: state.customer.customerLoading,
        updateCustomerLoading: state.customer.updateCustomerLoading,
      };
    },
    shallowEqual
  );

  const fetchData = async () => {
    try {
      await dispatch(fetchCustomer(id)).unwrap();
      validation.resetForm();
    } catch (error) {
      toggle();
    }
  };

  useEffect(() => {
    if (isOpen && id) {
      fetchData();
    }
  }, [isOpen, id, dispatch]);

  // validation
  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      id: id,
      firstName: customer?.firstName,
      lastName: customer?.lastName,
      phoneNumber: customer?.phoneNumber,
      plateNumber: customer?.plateNumber,
      email: customer?.email,
      companyId: customer?.companyId,
      isActive: customer?.isActive,
    },

    validationSchema: Yup.object({
      firstName: Yup.string()
        .max(50, "Ad en fazla 50 karakter olabilir.")
        .required("Lütfen müşteri adını giriniz."),
      lastName: Yup.string()
        .max(50, "Soyad en fazla 50 karakter olabilir.")
        .required("Lütfen müşteri soyadını giriniz."),
      plateNumber: Yup.string()
        .max(20, "Plaka en fazla 20 karakter olabilir.")
        .required("Lütfen plaka numarasını giriniz."),
      phoneNumber: Yup.string()
        .max(20, "Telefon numarası en fazla 20 karakter olabilir.")
        .required("Lütfen telefon numarası giriniz."),
      email: Yup.string()
        .email("Lütfen geçerli bir e-posta adresi giriniz.")
        .max(100, "E-posta en fazla 100 karakter olabilir.")
        .notRequired(),
    }),

    onSubmit: async (values) => {
      await dispatch(updateCustomer(values)).unwrap();
      toggle(false);
    },
  });

  const handleCarPlateChange = (e) => {
    const value = e.target.value.replace(/\s/g, "");
    validation.setFieldValue("plateNumber", value);
  };

  if (customerLoading) {
    return null;
  }

  return (
    <Modal size="lg" isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader
        className="modal-title"
        id="myLargeModalLabel"
        toggle={toggle}
      >
        Müşteri Bilgilerini Düzenle
      </ModalHeader>

      <ModalBody className="p-4">
        <Form onSubmit={validation.handleSubmit} autoComplete="off">
          <Row className="gy-3">
            <Col lg={6}>
              <Label>Ad</Label>
              <Input
                type="text"
                name="firstName"
                placeholder="Can"
                maxLength={50}
                value={validation.values.firstName}
                onChange={validation.handleChange}
                invalid={
                  !!(
                    validation.touched.firstName && validation.errors.firstName
                  )
                }
              />
              <FormFeedback>{validation.errors.firstName}</FormFeedback>
            </Col>

            <Col lg={6}>
              <Label>Soyad</Label>
              <Input
                type="text"
                name="lastName"
                placeholder="Yılmaz"
                maxLength={50}
                value={validation.values.lastName}
                onChange={validation.handleChange}
                invalid={
                  !!(validation.touched.lastName && validation.errors.lastName)
                }
              />
              <FormFeedback>{validation.errors.lastName}</FormFeedback>
            </Col>

            <Col lg={6}>
              <Label>Telefon Numarası</Label>
              <InputGroup>
                <span
                  className="input-group-text"
                  style={{
                    width: "44px",
                    justifyContent: "center",
                  }}
                >
                  0
                </span>
                <IMaskInput
                  inputMode="numeric"
                  mask="000 000 00 00"
                  unmask={true}
                  name="phoneNumber"
                  placeholder="5XX XXX XX XX"
                  value={validation.values.phoneNumber}
                  className={`form-control ${validation.touched.phoneNumber &&
                      validation.errors.phoneNumber
                      ? "is-invalid"
                      : ""
                    }`}
                  style={{ zIndex: 0 }}
                  onAccept={(value) =>
                    validation.setFieldValue("phoneNumber", value)
                  }
                />
              </InputGroup>
              {validation.touched.phoneNumber &&
                validation.errors.phoneNumber && (
                  <div className="invalid-feedback d-block">
                    {validation.errors.phoneNumber}
                  </div>
                )}
            </Col>

            <Col lg={6}>
              <Label>E-posta Adresi</Label>
              <Input
                type="email"
                name="email"
                placeholder="musteri@ornek.com"
                maxLength={100}
                value={validation.values.email}
                onChange={validation.handleChange}
                invalid={
                  !!(validation.touched.email && validation.errors.email)
                }
              />
              <FormFeedback>{validation.errors.email}</FormFeedback>
            </Col>

            <Col lg={6}>
              <Label>Plaka</Label>
              <Input
                type="text"
                name="plateNumber"
                className="form-control"
                placeholder="38ABC000"
                maxLength={20}
                value={validation.values.plateNumber || ""}
                onChange={handleCarPlateChange}
                onKeyDown={(e) => {
                  if (e.key === " " || e.code === "Space") {
                    e.preventDefault();
                  }
                }}
                invalid={
                  !!(
                    validation.touched.plateNumber &&
                    validation.errors.plateNumber
                  )
                }
              />
              <FormFeedback>{validation.errors.plateNumber}</FormFeedback>
            </Col>
          </Row>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button
              type="button"
              color="primary"
              onClick={() => validation.handleSubmit()}
              className="btn btn-label"
              disabled={updateCustomerLoading}
            >
              <i className="ri-check-line label-icon align-middle me-2"></i>{" "}
              Kaydet
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default UpdateModal;
